/**
 * Client service for the Broadcasts feature.
 *
 * `callSendBroadcast` posts directly to the iv3api notifications endpoint
 * (via the `iv3Request` interceptor, which attaches the operator token and
 * auto-refreshes on 401) and then writes the published `notificationHistory`
 * row + activity entry client-side. Firestore rules gate those writes to
 * managers (see firestore.rules).
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { firestore } from "./firebase_config";
import { Constants } from "../constant";
import {
  ActivityDomains,
  appendActivity,
  type ActivityActor,
} from "./activityService";
import { iv3Request } from "./iv3Client";
import { getBroadcastEnvInfo } from "./notificationRoutes";
import { BROADCAST_TYPE_TO_PROTO } from "src/types/broadcasts";
import type {
  BroadcastEnv,
  BroadcastFormState,
  DataPayload,
  NotificationHistoryDoc,
  NotificationHistoryListItem,
  SendBroadcastRequest,
  SendBroadcastResponse,
} from "src/types/broadcasts";

/** Build the iv3api request body the preview pane mirrors. */
export function buildApiRequestBody(
  form: BroadcastFormState,
): Record<string, unknown> {
  const payload = buildDataPayload(form);
  const body: Record<string, unknown> = {
    type:
      form.type === "NEWS"
        ? "MOBILE_NOTIFICATION_TYPE_NEWS"
        : "MOBILE_NOTIFICATION_TYPE_PROMOTIONAL",
    title: form.title.trim(),
    body: form.body.trim(),
  };
  if (payload) body.dataPayload = payload;
  return body;
}

/** Derive the structured `dataPayload` (or null) from form state. */
export function buildDataPayload(form: BroadcastFormState): DataPayload | null {
  if (form.tapKind === "none") return null;
  if (form.tapKind === "URL_LAUNCH") {
    return form.url.trim()
      ? { type: "URL_LAUNCH", target: form.url.trim() }
      : null;
  }
  if (!form.navTarget) return null;
  const params = parseParamsText(form.navParamsText);
  const out: DataPayload = {
    type: "NAVIGATION",
    target: form.navTarget,
    ...(form.navEvent ? { event: form.navEvent } : {}),
    ...(params ? { params } : {}),
  };
  return out;
}

/**
 * Parses the params textarea — accepts JSON object literal. Returns null on
 * empty input, throws an Error on invalid JSON so the form can surface it.
 */
export function parseParamsText(text: string): Record<string, unknown> | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    throw new Error(
      'Params must be valid JSON (e.g. { "highlight_ids": ["vue-1"] })',
    );
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Params must be a JSON object");
  }
  return parsed as Record<string, unknown>;
}

/** Validates form locally before calling the function — short-circuits a round-trip. */
export function validateForm(
  form: BroadcastFormState,
  authToken: string,
  tokenEnv: BroadcastEnv | null = null,
): string | null {
  if (!authToken.trim()) return "Add an auth token (paste or verify by email).";
  if (tokenEnv && tokenEnv !== form.env) {
    return `Auth token is for ${tokenEnv}. Verify or paste a token for ${form.env}.`;
  }
  if (!form.title.trim()) return "Title is required.";
  if (!form.body.trim()) return "Body is required.";
  if (form.tapKind === "URL_LAUNCH") {
    const url = form.url.trim();
    if (!url) return "URL is required for URL_LAUNCH.";
    try {
      const u = new URL(url);
      if (u.protocol !== "http:" && u.protocol !== "https:") {
        return "URL must start with http(s)://";
      }
    } catch {
      return "URL is not valid.";
    }
  }
  if (form.tapKind === "NAVIGATION" && !form.navTarget) {
    return "Pick a navigation target.";
  }
  if (form.tapKind === "NAVIGATION") {
    try {
      parseParamsText(form.navParamsText);
    } catch (e) {
      return e instanceof Error ? e.message : "Invalid params JSON";
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Drafts
//
// Draft rows live in the same `notificationHistory` collection as published
// rows, distinguished by `status: "draft"`. Firestore rules permit operators
// with MANAGE_BROADCASTS to create + update only when `status === "draft"`;
// promotion to "published" is the `sendBroadcast` callable's job.
// ---------------------------------------------------------------------------

/**
 * Persist a draft. Pass `existingId` to update an in-progress draft (operator
 * came in via "Edit" on a draft row); omit it for a fresh save.
 *
 * Returns the doc id (new or existing) so the composer can keep editing the
 * same row across multiple saves.
 */
export async function saveBroadcastDraft(
  form: BroadcastFormState,
  actor: ActivityActor,
  existingId?: string,
): Promise<string> {
  // Drafts can be incomplete (title/body might still be blank). buildDataPayload
  // already returns null for unfinished tapKind state, so the doc shape stays
  // valid even mid-edit. The hard validation only runs on Send.
  const dataPayload = buildDataPayload(form);
  const payload: Record<string, unknown> = {
    env: form.env,
    type: form.type,
    title: form.title.trim(),
    body: form.body.trim(),
    dataPayload,
    status: "draft",
    apiNotificationId: null,
    actor: {
      uid: actor.uid,
      email: actor.email,
      displayName: actor.displayName ?? null,
    },
  };
  const col = collection(firestore, Constants.NOTIFICATION_HISTORY_COLLECTION);
  let id: string;
  if (existingId) {
    const ref = doc(col, existingId);
    // `setDoc` (not update) so the rules see the full shape on update. We
    // refresh `createdAt` on every save so the draft floats to the top — the
    // history list orders by createdAt desc.
    await setDoc(ref, { ...payload, createdAt: serverTimestamp() });
    id = existingId;
  } else {
    const ref = await addDoc(col, { ...payload, createdAt: serverTimestamp() });
    id = ref.id;
  }
  try {
    await appendActivity({
      domain: ActivityDomains.Broadcasts,
      action: existingId ? "update" : "create",
      resourceType: "broadcast_draft",
      resourceId: id,
      summary: existingId
        ? `Updated ${form.type} draft: "${form.title || "(untitled)"}"`
        : `Saved ${form.type} draft: "${form.title || "(untitled)"}"`,
      actor,
      details: { env: form.env, type: form.type, tapKind: form.tapKind },
    });
  } catch (e) {
    console.error("Failed to log draft activity:", e);
  }
  return id;
}

/** Discard a draft doc (alias on top of deleteBroadcastRecord with a different activity message). */
export async function deleteBroadcastDraft(
  item: NotificationHistoryListItem,
  actor: ActivityActor | null,
): Promise<void> {
  await deleteDoc(
    doc(firestore, Constants.NOTIFICATION_HISTORY_COLLECTION, item.id),
  );
  if (actor) {
    try {
      await appendActivity({
        domain: ActivityDomains.Broadcasts,
        action: "delete",
        resourceType: "broadcast_draft",
        resourceId: item.id,
        summary: `Deleted ${item.type} draft: "${item.title || "(untitled)"}"`,
        actor,
        details: { env: item.env, type: item.type },
      });
    } catch (e) {
      console.error("Failed to log draft delete activity:", e);
    }
  }
}

const NOTIFICATIONS_PATH = "/api/v2/mobile/notifications";

/**
 * Send a broadcast by calling the iv3api notifications endpoint directly from
 * the client (previously proxied through the `sendBroadcast` Firebase callable).
 *
 * The operator token is attached by the `iv3Request` interceptor via the
 * `X_IVUEIT_AUTH_TOKEN` header (underscore form — the one the nginx ingress
 * allows through CORS preflight and the backend reads), so a 401 triggers a
 * single refresh-and-retry. On success we persist the published
 * `notificationHistory` row and append an activity entry directly (Firestore
 * rules allow managers to write published rows). Failed sends throw and are
 * not persisted.
 */
export async function callSendBroadcast(
  req: SendBroadcastRequest,
  actor: ActivityActor | null,
): Promise<SendBroadcastResponse> {
  const apiBody: Record<string, unknown> = {
    type: BROADCAST_TYPE_TO_PROTO[req.type],
    title: req.title,
    body: req.body,
  };
  if (req.dataPayload) apiBody.dataPayload = req.dataPayload;

  const res = await iv3Request<{ id?: unknown }>(req.env, NOTIFICATIONS_PATH, {
    method: "POST",
    body: JSON.stringify(apiBody),
    auth: true,
    authHeader: "X_IVUEIT_AUTH_TOKEN",
    authScheme: "raw",
    baseUrl: (env) => getBroadcastEnvInfo(env).baseUrl,
  });
  const apiNotificationId = typeof res.id === "string" ? res.id : null;

  // The push has already gone out. If persisting the history row fails, make it
  // unmistakable that the send SUCCEEDED — otherwise the operator sees a
  // generic failure and re-sends, broadcasting to every device again.
  let historyRef;
  try {
    historyRef = await addDoc(
      collection(firestore, Constants.NOTIFICATION_HISTORY_COLLECTION),
      {
        env: req.env,
        type: req.type,
        title: req.title,
        body: req.body,
        dataPayload: req.dataPayload,
        status: "published",
        apiNotificationId,
        actor: {
          uid: actor?.uid ?? "",
          email: actor?.email ?? "",
          displayName: actor?.displayName ?? null,
        },
        createdAt: serverTimestamp(),
      },
    );
  } catch (e) {
    throw new Error(
      "Broadcast was SENT but could not be recorded — do NOT resend. " +
        (e instanceof Error ? e.message : String(e)),
    );
  }

  // Audit log — best effort, never fail the send on a logging error.
  if (actor) {
    try {
      await appendActivity({
        domain: ActivityDomains.Broadcasts,
        action: "create",
        resourceType: "broadcast",
        resourceId: historyRef.id,
        summary: `Sent ${req.type} broadcast to ${req.env}: "${req.title}"`,
        actor,
        details: {
          env: req.env,
          type: req.type,
          tapKind: req.dataPayload?.type ?? "none",
          apiNotificationId: apiNotificationId ?? undefined,
        },
      });
    } catch (e) {
      console.error("Failed to log broadcast send activity:", e);
    }
  }

  return { historyId: historyRef.id, status: "published", apiNotificationId };
}

const HISTORY_PAGE_SIZE = 20;

interface HistoryPage {
  items: NotificationHistoryListItem[];
  cursor: QueryDocumentSnapshot | null;
  hasMore: boolean;
}

export async function deleteBroadcastRecord(
  item: NotificationHistoryListItem,
  actor: ActivityActor | null,
): Promise<void> {
  await deleteDoc(
    doc(firestore, Constants.NOTIFICATION_HISTORY_COLLECTION, item.id),
  );
  if (actor) {
    try {
      await appendActivity({
        domain: ActivityDomains.Broadcasts,
        action: "delete",
        resourceType: "broadcast",
        resourceId: item.id,
        summary: `Deleted ${item.type} broadcast history entry: "${item.title}"`,
        actor,
        details: { env: item.env, type: item.type },
      });
    } catch (e) {
      console.error("Failed to log broadcast delete activity:", e);
    }
  }
}

/**
 * Reconstruct the full iv3api request body from a stored history row so the
 * expanded view shows the exact payload that was POSTed.
 */
export function historyItemToFullPayload(
  item: NotificationHistoryListItem,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    type:
      item.type === "NEWS"
        ? "MOBILE_NOTIFICATION_TYPE_NEWS"
        : "MOBILE_NOTIFICATION_TYPE_PROMOTIONAL",
    title: item.title,
    body: item.body,
  };
  if (item.dataPayload) body.dataPayload = item.dataPayload;
  return body;
}

/** Map a stored history doc back to composer form state for duplication. */
export function historyItemToFormPrefill(
  item: NotificationHistoryListItem,
): Partial<BroadcastFormState> {
  const base: Partial<BroadcastFormState> = {
    env: item.env,
    type: item.type,
    title: item.title,
    body: item.body,
    tapKind: "none",
    navTarget: "",
    navEvent: "",
    navParamsText: "",
    url: "",
  };
  const payload = item.dataPayload;
  if (!payload) return base;
  if (payload.type === "URL_LAUNCH") {
    return { ...base, tapKind: "URL_LAUNCH", url: payload.target };
  }
  return {
    ...base,
    tapKind: "NAVIGATION",
    navTarget: payload.target,
    navEvent: payload.event ?? "",
    navParamsText: payload.params
      ? JSON.stringify(payload.params, null, 2)
      : "",
  };
}

export async function fetchBroadcastHistory(
  cursor: QueryDocumentSnapshot | null,
): Promise<HistoryPage> {
  const col = collection(firestore, Constants.NOTIFICATION_HISTORY_COLLECTION);
  const q = cursor
    ? query(
        col,
        orderBy("createdAt", "desc"),
        startAfter(cursor),
        limit(HISTORY_PAGE_SIZE),
      )
    : query(col, orderBy("createdAt", "desc"), limit(HISTORY_PAGE_SIZE));
  const snap = await getDocs(q);
  const items: NotificationHistoryListItem[] = snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as NotificationHistoryDoc),
  }));
  return {
    items,
    cursor: snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null,
    hasMore: snap.docs.length === HISTORY_PAGE_SIZE,
  };
}
